'use client';

import { useEffect, useState } from 'react';
import { MfaLoginState } from '@/hooks/useAuthHooks';
import { UseMutationResult } from '@tanstack/react-query';

const MFA_RESEND_LOCKOUT_KEY = 'mfa_resend_locked_until';
const MFA_RESEND_LOCKOUT_DURATION_MS = 10 * 60 * 1000;
const MAX_SEND_ATTEMPTS_MESSAGE =
  'Too many attempts. Please try again in 10 minutes.';

type UseMfaLoginModalProps = {
  mfaLoginState: MfaLoginState;
  verifyMfaLoginMutation: UseMutationResult<
    unknown,
    Error,
    { otp: string; mfaToken: string }
  >;
  resendMfaLoginMutation: UseMutationResult<unknown, Error, string>;
};

export default function useMfaLoginModal({
  mfaLoginState,
  verifyMfaLoginMutation,
  resendMfaLoginMutation
}: UseMfaLoginModalProps) {
  const [otp, setOtp] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(60);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const stored = localStorage.getItem(MFA_RESEND_LOCKOUT_KEY);
    if (!stored) return;
    const remaining = Math.ceil((Number(stored) - Date.now()) / 1000);
    if (remaining > 0) {
      setResendCooldown(remaining);
    } else {
      localStorage.removeItem(MFA_RESEND_LOCKOUT_KEY);
    }
  }, []);

  useEffect(() => {
    if (resendCooldown <= 0) {
      localStorage.removeItem(MFA_RESEND_LOCKOUT_KEY);
      return;
    }
    const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleVerify = async () => {
    if (otp.length < 6) return;
    setError(null);
    try {
      await verifyMfaLoginMutation.mutateAsync({
        otp,
        mfaToken: mfaLoginState.mfaToken
      });
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Invalid or expired code. Please try again.'
      );
      setOtp('');
    }
  };

  const handleResend = async () => {
    setError(null);
    try {
      await resendMfaLoginMutation.mutateAsync(mfaLoginState.mfaToken);
      setResendCooldown(60);
      setOtp('');
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Failed to resend code. Please try again.';
      setError(message);
      if (message === MAX_SEND_ATTEMPTS_MESSAGE) {
        const lockedUntil = Date.now() + MFA_RESEND_LOCKOUT_DURATION_MS;
        localStorage.setItem(MFA_RESEND_LOCKOUT_KEY, String(lockedUntil));
        setResendCooldown(Math.ceil(MFA_RESEND_LOCKOUT_DURATION_MS / 1000));
      }
    }
  };

  return {
    otp,
    setOtp,
    error,
    resendCooldown,
    handleVerify,
    handleResend,
    isVerifying: verifyMfaLoginMutation.isPending,
    isResending: resendMfaLoginMutation.isPending
  };
}
